import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { BarChart3, CheckCircle, Clock, Share2, XCircle } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Card, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../contexts';
import ShareMenu from './ShareMenu';

const CivicMarketCard = ({ market, initialResult }) => {
  const { user } = useAuth();
  const [voteData, setVoteData] = useState(initialResult || null);
  const [shareOpen, setShareOpen] = useState(false);
  const [voteHistory, setVoteHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // initialResult kommt aus dem Bulk-Fetch der Listen-Seiten und
  // vermeidet den N+1-Call (ein /civic/results pro Karte). Ohne Prop
  // (z.B. Detail-/Standalone-Nutzung) faellt die Karte auf Self-Fetch.
  useEffect(() => {
    if (initialResult) { setVoteData(initialResult); return; }
    fetchResults();
  }, [market.id, initialResult]);

  const fetchResults = async () => {
    try {
      const res = await api.civic.getResults(market.id);
      setVoteData(res.data);
    } catch {}
  };

  const openHistory = async () => {
    try {
      const res = await api.civic.getVoteHistory(market.id, { days: 30 });
      setVoteHistory(res.data || []);
      setShowHistory(true);
    } catch {
      toast.error('Verlauf konnte nicht geladen werden');
    }
  };

  const handleVote = async (direction) => {
    if (!user) { toast.error('Bitte einloggen zum Abstimmen'); return; }
    try {
      const res = await api.civic.vote(market.id, direction);
      setVoteData(res.data);
      toast.success(direction === 'yes' ? 'Du hast mit JA gestimmt' : 'Du hast mit NEIN gestimmt');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Abstimmen');
    }
  };

  const getShareText = () => {
    const pct = voteData ? voteData.yes_percent : 50;
    return `${pct}% sagen JA zu: ${market.title}`;
  };

  const getShareUrl = () => {
    const ref = user ? `?ref=${user.handle}` : '';
    return `${window.location.origin}/markets${ref}`;
  };

  const yesPct = voteData?.yes_percent ?? 50;
  const noPct = voteData?.no_percent ?? 50;
  const totalVotes = voteData?.total_votes ?? (market.yes_votes + market.no_votes);
  const userVote = voteData?.user_vote;
  const isClosed = market.status === 'closed' || market.status === 'settled';

  const getTimeDisplay = () => {
    if (isClosed) return 'Beendet';
    if (!market.closes_at) return 'Offen';
    const diff = new Date(market.closes_at) - new Date();
    if (diff <= 0) return 'Beendet';
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    return days > 0 ? `${days}d ${hours}h` : `${hours}h`;
  };

  return (
    <Card className="group hover:shadow-lg dark:hover:shadow-black/40 transition-all duration-300 relative overflow-hidden border-gray-200 dark:border-slate-800 dark:bg-slate-900/80 dark:backdrop-blur-sm dark:border-slate-700" data-testid={`civic-card-${market.id}`}>
      <div className="p-4 space-y-3">
        {/* Header-Reihe analog zu MarketCard, mit Civic-Badge in Blau */}
        <div className="flex gap-3">
          {market.image_url && (
            <div className="flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
              <img
                src={market.image_url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base sm:text-lg leading-snug text-gray-900 dark:text-gray-100 line-clamp-2">
              {market.title}
            </CardTitle>
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1.5 text-xs">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-md border border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300 text-[10px] font-bold uppercase tracking-wide">
                Civic
              </span>
              <span className="text-gray-500 dark:text-gray-400">{market.civic_category || 'Civic'}</span>
              {userVote ? (
                <Badge variant="outline" className="text-[10px] ml-auto text-blue-600 dark:text-blue-300 border-blue-600 dark:border-blue-500/40 bg-blue-50 dark:bg-blue-500/10 dark:text-blue-400">
                  Abgestimmt
                </Badge>
              ) : (
                <div className={`ml-auto inline-flex items-center gap-1 ${isClosed ? 'text-rose-600 dark:text-rose-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                  <Clock className="h-3 w-3" />
                  {getTimeDisplay()}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1.5">
              Kostenlose Abstimmung — keine Credits nötig.
            </p>
          </div>
        </div>

        {/* Vote-Action: JA/NEIN als zwei gleich breite Bloecke. User-Vote-
            Highlight via ring, Closed-State via opacity. */}
        <div className={`grid grid-cols-2 gap-2 ${isClosed ? 'opacity-60' : ''}`}>
          <button
            type="button"
            disabled={isClosed}
            data-testid={`civic-vote-yes-${market.id}`}
            onClick={() => !isClosed && handleVote('yes')}
            className={`flex flex-col items-start px-3 py-2.5 rounded-lg border transition-all ${
              userVote === 'yes'
                ? 'bg-emerald-100 dark:bg-emerald-500/20 border-emerald-400 dark:border-emerald-500/50 ring-2 ring-emerald-300 dark:ring-emerald-500/40'
                : isClosed
                ? 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 cursor-not-allowed'
                : 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 cursor-pointer'
            }`}
          >
            <div className="flex items-baseline justify-between w-full">
              <span className={`text-xs font-bold tracking-wide ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-emerald-700 dark:text-emerald-300'}`}>
                JA
              </span>
              <span className={`text-lg font-bold ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-emerald-800 dark:text-emerald-200'}`}>
                {yesPct.toFixed(0)}%
              </span>
            </div>
            <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              {Math.round(totalVotes * yesPct / 100)} Stimmen
            </span>
          </button>
          <button
            type="button"
            disabled={isClosed}
            data-testid={`civic-vote-no-${market.id}`}
            onClick={() => !isClosed && handleVote('no')}
            className={`flex flex-col items-start px-3 py-2.5 rounded-lg border transition-all ${
              userVote === 'no'
                ? 'bg-rose-100 dark:bg-rose-500/20 border-rose-400 dark:border-rose-500/50 ring-2 ring-rose-300 dark:ring-rose-500/40'
                : isClosed
                ? 'bg-gray-50 dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 cursor-not-allowed'
                : 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 hover:bg-rose-100 dark:hover:bg-rose-500/20 cursor-pointer'
            }`}
          >
            <div className="flex items-baseline justify-between w-full">
              <span className={`text-xs font-bold tracking-wide ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-rose-700 dark:text-rose-300'}`}>
                NEIN
              </span>
              <span className={`text-lg font-bold ${isClosed ? 'text-gray-500 dark:text-gray-400' : 'text-rose-800 dark:text-rose-200'}`}>
                {noPct.toFixed(0)}%
              </span>
            </div>
            <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              {Math.round(totalVotes * noPct / 100)} Stimmen
            </span>
          </button>
        </div>

        {/* Footer: Stimmen-Count links, Verlauf/Teilen-Icons rechts. */}
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100 dark:border-slate-800 text-[11px] text-gray-500 dark:text-gray-400">
          <span>{totalVotes.toLocaleString('de-DE')} Stimmen</span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={openHistory}
              data-testid={`civic-chart-btn-${market.id}`}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              aria-label="Stimmverlauf"
              title="Verlauf"
            >
              <BarChart3 size={14} />
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShareOpen(!shareOpen); }}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                data-testid={`civic-share-${market.id}`}
                aria-label="Teilen"
                title="Teilen"
              >
                <Share2 size={14} />
              </button>
              {shareOpen && (
                <ShareMenu
                  text={getShareText()}
                  url={getShareUrl()}
                  onClose={() => setShareOpen(false)}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* JA/NEIN-Buttons sind in die linke Header-Spalte gewandert
          unter den Title; Stimmen/Verlauf/Teilen liegen in der
          rechten Header-Spalte. CardContent bleibt nur für ggf.
          Friends-Activity oder andere zukünftige Inhalte. */}

      {/* Vote-history dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pr-6">
            <DialogTitle className="text-base sm:text-lg leading-tight text-left">
              <span className="line-clamp-2">Stimmverlauf — {market.title}</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-left">
              Tägliche JA-Quote über die letzten 30 Tage
            </DialogDescription>
          </DialogHeader>

          {voteHistory && (() => {
            const series = voteHistory;
            const latest = series.length > 0 ? series[series.length - 1] : null;
            const yesPct = latest ? (latest.yes_pct * 100).toFixed(1) : null;
            const noPct = latest ? (100 - latest.yes_pct * 100).toFixed(1) : null;
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-around text-sm">
                  <div className="flex items-center gap-2 text-blue-700 font-semibold dark:text-blue-300">
                    <CheckCircle className="h-4 w-4" />
                    JA <span className="font-mono">{yesPct ?? '—'}%</span>
                  </div>
                  <div className="flex items-center gap-2 text-rose-700 font-semibold dark:text-rose-400">
                    <XCircle className="h-4 w-4" />
                    NEIN <span className="font-mono">{noPct ?? '—'}%</span>
                  </div>
                  {latest && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {latest.total.toLocaleString()} Stimmen
                    </div>
                  )}
                </div>

                {series.length > 1 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={series}>
                      <defs>
                        <linearGradient id="civicGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(val) => val.slice(5)} />
                      <YAxis
                        domain={[0, 1]}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          `${(value * 100).toFixed(1)}% JA`,
                          'Quote',
                        ]}
                        labelFormatter={(lbl) => `Datum: ${lbl}`}
                      />
                      <Area
                        type="monotone"
                        dataKey="yes_pct"
                        stroke="#2563eb"
                        fill="url(#civicGradient)"
                        strokeWidth={2}
                        dot={{ r: 3, stroke: '#2563eb', fill: '#2563eb' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : series.length === 1 ? (
                  <div className="h-32 flex flex-col items-center justify-center text-gray-600 bg-gray-50 rounded-lg gap-1 dark:bg-slate-800/60 dark:text-gray-400">
                    <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">{yesPct}%</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {latest.date} — noch nur ein Tag, mehr Daten ab morgen
                    </div>
                  </div>
                ) : (
                  <div className="h-32 flex items-center justify-center text-gray-500 bg-gray-50 rounded-lg dark:bg-slate-800/60 dark:text-gray-400">
                    Noch keine Stimmen
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CivicMarketCard;
